import React, { useEffect } from 'react';

import Router from '@/router';

import useNetwork from '@/hooks/useNetwork';

import { Layout, notification, ConfigProvider, theme, App as AntdApp } from 'antd';

import Navigation from '@/app/Navigation';

import { useDispatch, useSelector } from 'react-redux';
import { selectAuth, selectCurrentAdmin } from '@/redux/auth/selectors';
import { selectIsDarkMode } from '@/redux/theme';
import HeaderContent from '@/app/HeaderContent';
import { fetchRoles } from '@/redux/rolesPermissions';
import GlobalProfileAlert from '@/components/GlobalProfileAlert';
import FirstLoginWelcomeModal from '@/components/FirstLoginWelcomeModal';
import GlobalReminderModal from '@/components/Notification/GlobalReminderModal';
import { ThemeProvider } from '@/context/theme';
import '@/styles/darkModeNotifications.css';
import { setAntdInstances } from '@/utils/notificationHelper';

const AntdGlobalSync = () => {
  const { message, notification, modal } = AntdApp.useApp();

  useEffect(() => {
    setAntdInstances(message, notification);
  }, [message, notification]);

  return null;
};

function App() {
  const { isLoggedIn } = useSelector(selectAuth);
  const dispatch = useDispatch()
  const isDarkMode = useSelector(selectIsDarkMode);

  // const [isOnline] = useNetwork();
  // // const networkState = useNetworkState();

  // if (!isOnline) {
  //   notification.config({
  //     duration: 0,
  //   });
  //   notification.error({
  //     message: "No internet connection",
  //     description: "Cannot connect to the server, Check your internet network",
  //   });
  // }

  const themeConfig = {
    algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: isDarkMode ? '#177ddc' : '#1890ff',
      colorSuccess: isDarkMode ? '#52c41a' : '#52c41a',
      colorWarning: isDarkMode ? '#faad14' : '#faad14',
      colorError: isDarkMode ? '#ff4d4f' : '#ff4d4f',
      colorInfo: isDarkMode ? '#1890ff' : '#1890ff',
    },
    components: {
      Notification: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? '#ffffff' : '#000000',
        colorTextDescription: isDarkMode ? '#a6a6a6' : '#00000073',
        borderRadius: 6,
        boxShadow: isDarkMode
          ? '0 6px 16px 0 rgba(0, 0, 0, 0.48), 0 3px 6px -4px rgba(0, 0, 0, 0.48), 0 9px 28px 8px rgba(0, 0, 0, 0.36)'
          : '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
      },
      Message: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? '#ffffff' : '#000000',
        colorTextDescription: isDarkMode ? '#a6a6a6' : '#00000073',
        borderRadius: 6,
        boxShadow: isDarkMode
          ? '0 3px 6px -4px rgba(0, 0, 0, 0.48), 0 6px 16px 0 rgba(0, 0, 0, 0.48), 0 9px 28px 8px rgba(0, 0, 0, 0.36)'
          : '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
      },
      Alert: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? '#ffffff' : '#000000',
        colorTextDescription: isDarkMode ? '#a6a6a6' : '#00000073',
        borderRadius: 6,
        boxShadow: isDarkMode
          ? '0 2px 8px rgba(0, 0, 0, 0.15)'
          : '0 2px 8px rgba(0, 0, 0, 0.06)',
      },
      Modal: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? '#ffffff' : '#000000',
        colorTextDescription: isDarkMode ? '#a6a6a6' : '#00000073',
        borderRadius: 8,
      },
      Badge: {
        colorError: '#1677FF', // Force all red badges to blue globally
      },
    },
  };

  if (!isLoggedIn) return (
    <ThemeProvider>
      <ConfigProvider theme={themeConfig}>
        <AntdApp
          message={{ duration: 3, maxCount: 3, top: 80 }}
          notification={{ duration: 4.5, maxCount: 3, top: 80, placement: 'topRight' }}
        >
          <AntdGlobalSync />
          <Router />
        </AntdApp>
      </ConfigProvider>
    </ThemeProvider>
  );

  else {
    dispatch(fetchRoles())
    return (
      <ThemeProvider>
        <ConfigProvider theme={themeConfig}>
          <AntdApp
            message={{ duration: 3, maxCount: 3, top: 80 }}
            notification={{ duration: 4.5, maxCount: 3, top: 80, placement: 'topRight' }}
          >
            <AntdGlobalSync />
            <GlobalProfileAlert />
            <FirstLoginWelcomeModal />
            <GlobalReminderModal />
            <Layout style={{ minHeight: '100vh' }}>
              <Navigation />
              <Layout style={{ minHeight: '100vh' }}>
                <HeaderContent />
                <Router isLoggedIn={true} />
              </Layout>
            </Layout>
          </AntdApp>
        </ConfigProvider>
      </ThemeProvider>
    );
  }
}

export default App;